import { useState } from 'react';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useFormacao } from '@/context/FormacaoContext';
import type { Formacao } from '@/context/FormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

interface Props {
  formacoes: Formacao[];
  onClose: () => void;
}

type FormatoExport = 'csv' | 'excel';

// ─── Utils de exportação ──────────────────────────────────────────────────────
function escapeCsv(val: unknown): string {
  const s = val === null || val === undefined ? '' : String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsvContent(rows: Record<string, unknown>[], sep = ','): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.map(escapeCsv).join(sep),
    ...rows.map((r) => headers.map((h) => escapeCsv(r[h])).join(sep)),
  ];
  return lines.join('\n');
}

function downloadBlob(content: string, filename: string, mime: string) {
  const bom = mime.includes('csv') ? '\uFEFF' : '';
  const blob = new Blob([bom + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(d: string | null): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('pt-PT');
}

function buildRows(formacoes: Formacao[], participantes: import('@/context/FormacaoContext').FormacaoParticipante[], campos: Set<string>): Record<string, unknown>[] {
  return formacoes.map((f) => {
    const custoParticipantes = participantes
      .filter((p) => p.formacao_id === f.id)
      .reduce((s, p) => s + Number(p.custo || 0), 0);
    const row: Record<string, unknown> = {};
    if (campos.has('nome'))              row['Nome da Ação'] = f.nome;
    if (campos.has('categoria'))         row['Área Temática'] = f.categoria ?? '';
    if (campos.has('modalidade'))        row['Modalidade'] = f.modalidade;
    if (campos.has('entidade'))          row['Entidade Formadora'] = f.entidade_formadora ?? '';
    if (campos.has('data_inicio'))       row['Data Início'] = formatDate(f.data_inicio);
    if (campos.has('data_fim'))          row['Data Fim'] = formatDate(f.data_fim);
    if (campos.has('duracao_horas'))     row['Horas'] = f.duracao_horas;
    if (campos.has('estado'))            row['Estado'] = estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : estadoNormalizado(f.estado) === 'planeada' ? 'Planeada' : estadoNormalizado(f.estado) === 'em_curso' ? 'Em Curso' : f.estado;
    if (campos.has('participantes'))     row['Nº Participantes'] = f.total_participantes ?? 0;
    if (campos.has('custo'))             row['Custo Real (€)'] = custoParticipantes;
    if (campos.has('orcamento'))         row['Orçamento Previsto (€)'] = f.orcamento_previsto ?? 0;
    if (campos.has('financiamento'))     row['Financiamento'] = f.financiamento ?? '';
    if (campos.has('local'))             row['Local'] = f.local ?? '';
    return row;
  });
}

// Exportação Excel simples via HTML table (abre no Excel)
function buildExcelHtml(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const headerRow = headers.map((h) => `<th style="background:#18181b;color:#fff;font-weight:bold;padding:6px 10px;border:1px solid #ccc">${h}</th>`).join('');
  const dataRows = rows.map((r, i) => {
    const bg = i % 2 === 0 ? '#fff' : '#f9fafb';
    const cells = headers.map((h) => `<td style="padding:5px 10px;border:1px solid #e5e7eb;background:${bg}">${r[h] ?? ''}</td>`).join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"></head>
<body><table border="1"cellpadding="5"cellspacing="0"><thead><tr>${headerRow}</tr></thead><tbody>${dataRows}</tbody></table></body></html>`;
}

// ─── Campos disponíveis ───────────────────────────────────────────────────────
const CAMPOS = [
  { key: 'nome',          label: 'Nome da Ação',          required: true },
  { key: 'categoria',     label: 'Área Temática',          required: false },
  { key: 'modalidade',    label: 'Modalidade',             required: false },
  { key: 'entidade',      label: 'Entidade Formadora',     required: false },
  { key: 'data_inicio',   label: 'Data Início',            required: false },
  { key: 'data_fim',      label: 'Data Fim',               required: false },
  { key: 'duracao_horas', label: 'Horas',                  required: false },
  { key: 'estado',        label: 'Estado',                 required: false },
  { key: 'participantes', label: 'Nº Participantes',        required: false },
  { key: 'custo',         label: 'Custo Real (€)',         required: false },
  { key: 'orcamento',     label: 'Orçamento Previsto (€)', required: false },
  { key: 'financiamento', label: 'Financiamento',          required: false },
  { key: 'local',         label: 'Local',                  required: false },
];

export default function ExportarAcoesModal({ formacoes, onClose }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { participantes } = useFormacao();

  const [formato, setFormato] = useState<FormatoExport>('excel');
  const [selectedCampos, setSelectedCampos] = useState<Set<string>>(
    new Set(['nome', 'categoria', 'modalidade', 'entidade', 'data_inicio', 'data_fim', 'duracao_horas', 'estado', 'participantes', 'custo', 'orcamento'])
  );
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroAno, setFiltroAno] = useState<string>('todos');
  const [done, setDone] = useState(false);

  const anos = Array.from(new Set(formacoes.map((f) => f.data_inicio ? new Date(f.data_inicio).getFullYear() : null).filter(Boolean))).sort() as number[];

  function toggleCampo(key: string) {
    setSelectedCampos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function getFiltered(): Formacao[] {
    return formacoes.filter((f) => {
      if (filtroEstado !== 'todos' && f.estado !== filtroEstado) return false;
      if (filtroAno !== 'todos' && f.data_inicio) {
        if (new Date(f.data_inicio).getFullYear() !== Number(filtroAno)) return false;
      }
      return true;
    });
  }

  function handleExport() {
    const filtered = getFiltered();
    const rows = buildRows(filtered, participantes, selectedCampos);
    const ts = new Date().toISOString().slice(0, 10);

    if (formato === 'csv') {
      const content = buildCsvContent(rows);
      downloadBlob(content, `formacoes-${ts}.csv`, 'text/csv;charset=utf-8');
    } else {
      const html = buildExcelHtml(rows);
      downloadBlob(html, `formacoes-${ts}.xls`, 'application/vnd.ms-excel');
    }

    setDone(true);
    setTimeout(() => {
      setDone(false);
      onClose();
    }, 1200);
  }

  const filteredCount = getFiltered().length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[92vh] overflow-auto ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-download-2-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Exportar Ações de Formação</h2>
              <p className="text-[11px] text-zinc-400">CSV ou Excel · personalizável</p>
            </div>
          </div>
          <button onClick={handleClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-7 py-5 space-y-5">
          {/* Sucesso */}
          {done && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] px-4 py-3 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
              Ficheiro exportado com sucesso!
            </div>
          )}

          {/* Formato */}
          <div>
            <p className="text-[12px] font-bold text-zinc-700 mb-2">Formato de exportação</p>
            <div className="grid grid-cols-2 gap-2">
              {([
                { v: 'excel' as FormatoExport, icon: 'ri-table-2', label: 'Excel (.xls)', sub: 'Abre directamente no Excel' },
                { v: 'csv'   as FormatoExport, icon: 'ri-file-text-line', label: 'CSV (.csv)', sub: 'Compatível com todos os sistemas' },
              ] as const).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFormato(opt.v)}
                  className={`whitespace-nowrap flex items-start gap-3 p-3.5 rounded-xl border transition-all cursor-pointer text-left ${
                    formato === opt.v ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${formato === opt.v ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                    <i className={`${opt.icon} text-sm`} />
                  </div>
                  <div>
                    <p className={`text-[12px] font-bold ${formato === opt.v ? 'text-zinc-900' : 'text-zinc-600'}`}>{opt.label}</p>
                    <p className="text-[10px] text-zinc-400">{opt.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Filtros */}
          <div>
            <p className="text-[12px] font-bold text-zinc-700 mb-2">Filtrar dados</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 mb-1">Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="w-full px-3 py-2 text-[12px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none cursor-pointer"
                >
                  <option value="todos">Todos os estados</option>
                  <option value="concluida">Concluídas</option>
                  <option value="planeada">Planeadas</option>
                  <option value="em_curso">Em Curso</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 mb-1">Ano</label>
                <select
                  value={filtroAno}
                  onChange={(e) => setFiltroAno(e.target.value)}
                  className="w-full px-3 py-2 text-[12px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none cursor-pointer"
                >
                  <option value="todos">Todos os anos</option>
                  {anos.map((a) => <option key={a} value={String(a)}>{a}</option>)}
                </select>
              </div>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">
              <span className="font-semibold text-zinc-600">{filteredCount}</span> ação(ões) serão exportadas
            </p>
          </div>

          {/* Colunas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[12px] font-bold text-zinc-700">Colunas a incluir</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCampos(new Set(CAMPOS.map((c) => c.key)))}
                  className="whitespace-nowrap text-[11px] font-semibold text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer"
                >
                  Selecionar todas
                </button>
                <span className="text-zinc-200">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedCampos(new Set(CAMPOS.filter((c) => c.required).map((c) => c.key)))}
                  className="whitespace-nowrap text-[11px] font-semibold text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer"
                >
                  Só obrigatórias
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {CAMPOS.map((campo) => {
                const isOn = selectedCampos.has(campo.key);
                return (
                  <button
                    key={campo.key}
                    type="button"
                    onClick={() => !campo.required && toggleCampo(campo.key)}
                    disabled={campo.required}
                    className={`whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                      isOn
                        ? 'bg-zinc-900 border-zinc-900 text-white'
                        : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
                    } ${campo.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className={`w-3.5 h-3.5 flex items-center justify-center rounded flex-shrink-0 ${isOn ? 'bg-white/20' : 'bg-zinc-100'}`}>
                      {isOn && <i className="ri-check-line text-[10px]" />}
                    </div>
                    <span className="text-[11px] font-semibold truncate">{campo.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={filteredCount === 0 || selectedCampos.size === 0}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line text-sm" />
              </div>
              Exportar {filteredCount} ação(ões)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
